// Property Management → Month-End & Year-End.
//
// The recurring money operations that had library code and API routes but no
// screen, so running them needed a developer:
//
//   Fee cycle         preview → compute → managing broker authorises → sweep
//   Rent billings     preview → raise, across the rent roll; list by status
//   1099s             owner (1099-MISC rents) and vendor (1099-NEC) preview,
//                     generate drafts, download
//   Conversion check  every client ledger in every open trust account, as of a date
//
// Every step previews before it writes where the server can preview. The server
// decides who may do what (money-moving steps are the managing broker's alone);
// the buttons here only explain it, they do not enforce it.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select, Textarea,
    Button, Notice, Loading, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';

const API = '/api/property-management';
const pad = (n) => String(n).padStart(2, '0');
const now = new Date();
const LAST_MONTH = (() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
})();
const TODAY = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
const LAST_YEAR = now.getFullYear() - 1;

const CYCLE_TONE = { open: 'slate', computed: 'warn', authorised: 'indigo', swept: 'good', closed: 'good' };

const errNotice = (e) => ({ message: e.message, problems: e.payload?.problems });

export default function PMMonthEndPage() {
    const apiClient = useApiClient();
    const isMB = !!getCurrentUser()?.isManagingBroker;
    const params = new URLSearchParams(window.location.search);
    const [tab, setTab] = useState(params.get('tab') || 'fees');
    const [notice, setNotice] = useState(null);
    const [payWarnings, setPayWarnings] = useState([]);

    // Online payments: Stripe nets its fee out of rent payouts, so until the top-up is
    // transferred the trust bank holds less than the trust journal (68 IAC 1450.750).
    useEffect(() => {
        apiClient.get(`${API}/online-payments/summary`)
            .then((s) => setPayWarnings(s.warnings || []))
            .catch(() => setPayWarnings([]));
    }, [apiClient]);

    return (
        <div>
            <PageHeader
                title="Month-End & Year-End"
                subtitle="The monthly fee cycle, rent billings, year-end 1099s and the conversion check. Each one shows what it will do before it does it." />
            {payWarnings.length > 0 && (
                <Notice tone="warn" title="Online payments — settle before closing the month"
                    message="See Property Management → Online Payments."
                    problems={payWarnings} />
            )}
            {notice && <Notice tone={notice.tone || 'bad'} title={notice.title} message={notice.message}
                problems={notice.problems} onDismiss={() => setNotice(null)} />}
            <Tabs tabs={[
                { key: 'fees', label: 'Fee cycle' },
                { key: 'billings', label: 'Rent billings' },
                { key: '1099', label: '1099s' },
                { key: 'conversion', label: 'Conversion check' },
            ]} active={tab} onChange={(t) => { setTab(t); setNotice(null); }} />
            {tab === 'fees' && <FeeCycleTab api={apiClient} isMB={isMB} setNotice={setNotice} />}
            {tab === 'billings' && <BillingsTab api={apiClient} setNotice={setNotice} />}
            {tab === '1099' && <Tax1099Tab api={apiClient} isMB={isMB} setNotice={setNotice} />}
            {tab === 'conversion' && <ConversionTab api={apiClient} setNotice={setNotice} />}
        </div>
    );
}

// ===========================================================================
// Fee cycle
// ===========================================================================
function FeeCycleTab({ api, isMB, setNotice }) {
    const [loading, setLoading] = useState(true);
    const [cycles, setCycles] = useState([]);
    const [gate, setGate] = useState(null);
    const [operating, setOperating] = useState([]);
    const [month, setMonth] = useState(LAST_MONTH);
    const [preview, setPreview] = useState(null);
    const [detail, setDetail] = useState(null);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [c, g, a] = await Promise.all([
                api.get(`${API}/fee-cycles`),
                api.get(`${API}/fee-cycles/gate`).catch(() => null),
                api.get(`${API}/trust-accounts`).catch(() => []),
            ]);
            setCycles(c || []); setGate(g);
            setOperating((a || []).filter((x) => x.account_type === 'operating' && x.status !== 'closed'));
        } catch (e) { setNotice(errNotice(e)); } finally { setLoading(false); }
    }, [api, setNotice]);
    useEffect(() => { load(); }, [load]);

    const openCycle = async (id) => {
        try { setDetail(await api.get(`${API}/fee-cycles/${id}`)); }
        catch (e) { setNotice(errNotice(e)); }
    };

    const run = async (fn, after) => {
        setBusy(true); setNotice(null);
        try { const r = await fn(); await load(); if (after) await after(r); setModal(null); return r; }
        catch (e) { setNotice(errNotice(e)); return null; } finally { setBusy(false); }
    };

    const doPreview = () => run(async () => {
        const r = await api.get(`${API}/ops/fee-cycle-preview?month=${month}`);
        setPreview(r); return r;
    });
    const doCompute = () => run(
        () => api.post(`${API}/fee-cycles/compute`, { month: `${month}-01` }),
        async (r) => {
            setPreview(null);
            setNotice({ tone: r.warning ? 'warn' : 'good', title: 'Cycle computed',
                message: r.warning || `${r.books_included} client(s), ${money(r.total_fees)} posted to owner books. `
                    + 'Nothing has left trust. The managing broker authorises next.' });
            await openCycle(r.cycle_id);
        });
    const doAuthorise = () => run(
        () => api.post(`${API}/fee-cycles/${detail.cycle_id}/authorise`,
            { acknowledged_total: Number(detail.total_fees), note: form.note || undefined }),
        async () => { setNotice({ tone: 'good', message: 'Authorised. The sweep may now run.' }); await openCycle(detail.cycle_id); });
    const doSweep = () => run(
        () => api.post(`${API}/fee-cycles/${detail.cycle_id}/sweep`, { destination_account_id: form.destination_account_id }),
        async (r) => {
            setNotice({ tone: r.failures?.length ? 'warn' : 'good', title: `Swept ${money(r.total_swept)}`,
                message: `${r.clients_swept} client(s) swept to the operating account. ${r.note || ''}`,
                problems: (r.failures || []).map((f) => `Book ${f.book_id}: ${f.error}`) });
            await openCycle(detail.cycle_id);
        });

    if (loading) return <Loading />;
    const current = cycles[0];

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="This month's gate" value={gate ? (gate.ok ? 'Open' : 'Closed') : '—'}
                    tone={gate ? (gate.ok ? 'good' : 'warn') : 'default'}
                    sub={gate && !gate.ok ? `cycle ${gate.cycle_status}` : 'owner draws and payments may issue'} />
                <Stat label="Latest cycle" value={current ? fmtDate(current.period_start).replace(/ \d+,/, '') : 'none'}
                    sub={current ? titleCase(current.status) : 'compute the first month below'} />
                <Stat label="Latest total" value={current ? money(current.total_fees) : '—'}
                    sub={current?.authorised_by ? `authorised by ${current.authorised_by}` : 'not authorised'} />
                <Stat label="Latest swept" value={current ? money(current.total_swept) : '—'}
                    tone={current?.status === 'swept' ? 'good' : 'default'} />
            </div>

            {gate && !gate.ok && (
                <Notice tone="warn" title="Owner distributions are held this month" message={gate.message} />
            )}

            <Panel title="Run a month"
                subtitle="1. Preview (writes nothing) → 2. Compute (posts each property's fee to its owner's book; no money moves) → 3. Managing broker authorises the total → 4. Sweep to the operating account.">
                <div className="flex flex-wrap items-end gap-3">
                    <Input label="Month" type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPreview(null); }}
                        className="mb-0" />
                    <Button variant="ghost" disabled={busy || !month} onClick={doPreview}>Preview</Button>
                    <Button disabled={busy || !preview} onClick={() => setModal('compute')}
                        title={preview ? '' : 'Preview the month first'}>Compute…</Button>
                </div>
                {preview && (
                    <div className="mt-5">
                        {preview.existing_cycle && (
                            <Notice tone={['swept', 'closed'].includes(preview.existing_cycle.status) ? 'bad' : 'info'}
                                message={['swept', 'closed'].includes(preview.existing_cycle.status)
                                    ? `This month is already ${preview.existing_cycle.status}. It cannot be recomputed.`
                                    : `A cycle for this month already exists (${preview.existing_cycle.status}, ${money(preview.existing_cycle.total_fees)}). Computing again replaces its figures and any authorisation must be given again.`} />
                        )}
                        {preview.shortfalls.length > 0 && (
                            <Notice tone="warn" title={`${preview.shortfalls.length} client(s) cannot cover their fee`}
                                message="The managing broker cannot authorise until this is resolved: sweeping would take their ledger negative, which is another client's money."
                                problems={preview.shortfalls.map((s) => `${s.owner_name}: holds ${money(s.available)} against ${money(s.fee_total)}`)} />
                        )}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <Stat label="Total fees" value={money(preview.total_fees)} />
                            <Stat label="Clients" value={preview.books_included} />
                        </div>
                        <Table columns={[
                            { key: 'property', label: 'Property' },
                            { key: 'owner', label: 'Client' },
                            { key: 'collected', label: 'Collected', align: 'right', render: (r) => money(r.collected) },
                            { key: 'fee', label: 'Fee', align: 'right', render: (r) => money(r.fee) },
                            { key: 'note', label: 'Note', render: (r) => r.note
                                ? <span className="text-xs text-slate-500">{r.note}</span>
                                : <span className="text-xs text-slate-400">{r.basis ? `${titleCase(r.basis)}${r.rate ? ` ${Number(r.rate)}%` : ''} · ${r.receipts_counted} receipt(s)` : ''}</span> },
                        ]} rows={preview.detail.map((d) => ({ ...d, _key: d.pm_property_id }))}
                            empty="No managed property with a trust account." />
                    </div>
                )}
            </Panel>

            <Panel title="Cycles" subtitle="Click a month to authorise or sweep it.">
                <Table columns={[
                    { key: 'period_start', label: 'Month', nowrap: true, render: (r) => `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}` },
                    { key: 'status', label: 'Status', render: (r) => <Pill tone={CYCLE_TONE[r.status]}>{r.status}</Pill> },
                    { key: 'books_included', label: 'Clients', align: 'right' },
                    { key: 'total_fees', label: 'Fees', align: 'right', render: (r) => money(r.total_fees) },
                    { key: 'total_swept', label: 'Swept', align: 'right', render: (r) => money(r.total_swept) },
                    { key: 'authorised_by', label: 'Authorised by', render: (r) => r.authorised_by || '—' },
                ]} rows={cycles.map((c) => ({ ...c, _key: c.cycle_id }))} onRowClick={(r) => openCycle(r.cycle_id)}
                    empty="No cycle has been computed yet." />
            </Panel>

            {detail && !modal && (
                <Modal title={`Fee cycle ${fmtDate(detail.period_start)} – ${fmtDate(detail.period_end)}`}
                    subtitle={`Status: ${detail.status}`} onClose={() => setDetail(null)} wide>
                    <Table columns={[
                        { key: 'owner_name', label: 'Client' },
                        { key: 'account_name', label: 'Trust account' },
                        { key: 'fee_total', label: 'Fee', align: 'right', render: (r) => money(r.fee_total) },
                        { key: 'available_at_authorisation', label: 'Client holds', align: 'right', render: (r) => money(r.available_at_authorisation) },
                        { key: 'status', label: 'Status', render: (r) => (
                            <span>{Number(r.shortfall) === 1 && <Pill tone="bad">shortfall</Pill>} <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                                {r.skip_reason && <div className="text-xs text-slate-500 mt-1">{r.skip_reason}</div>}</span>) },
                    ]} rows={(detail.books || []).map((b) => ({ ...b, _key: b.cycle_book_id }))} empty="No client earned a fee this month." />
                    <div className="mt-4 text-sm text-slate-600">
                        Total <b>{money(detail.total_fees)}</b>
                        {detail.authorised_total !== null && detail.authorised_total !== undefined && <> · authorised {money(detail.authorised_total)} by {detail.authorised_by || '—'}</>}
                        {Number(detail.total_swept) > 0 && <> · swept {money(detail.total_swept)}</>}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                        A swept cycle cannot be reversed from this screen — there is no reversal route. If a sweep was wrong,
                        the correction is a new, documented transfer back into trust, made by the managing broker.
                    </p>
                    <div className="flex flex-wrap justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
                        {detail.status === 'computed' && (
                            <Button disabled={!isMB} title={isMB ? '' : 'Managing broker only'}
                                onClick={() => { setForm({}); setModal('authorise'); }}>Authorise…</Button>
                        )}
                        {detail.status === 'authorised' && (
                            <Button disabled={!isMB} title={isMB ? '' : 'Managing broker only'}
                                onClick={() => { setForm({ destination_account_id: operating[0]?.account_id || '' }); setModal('sweep'); }}>Sweep…</Button>
                        )}
                        {!isMB && ['computed', 'authorised'].includes(detail.status) && (
                            <span className="text-xs text-slate-500 self-center">The managing broker authorises and sweeps (68 IAC 1450.705).</span>
                        )}
                    </div>
                </Modal>
            )}

            {modal === 'compute' && preview && (
                <Modal title={`Compute ${month}`} onClose={() => setModal(null)}>
                    <p className="text-sm text-slate-700">
                        This posts <b>{money(preview.total_fees)}</b> in management fees to {preview.books_included} owner
                        book(s) for {fmtDate(preview.period_start)} – {fmtDate(preview.period_end)}. <b>No money moves.</b> The
                        fee sits in trust as due to the brokerage until the managing broker authorises and sweeps it.
                    </p>
                    <div className="flex justify-end gap-2 mt-5">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={doCompute}>{busy ? 'Computing…' : 'Compute and post fees'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'authorise' && detail && (
                <Modal title="Authorise the fee cycle" subtitle="Managing broker — 68 IAC 1450.705" onClose={() => setModal(null)}>
                    <p className="text-sm text-slate-700">
                        You are approving <b>{money(detail.total_fees)}</b> in earned management fees for
                        {' '}{fmtDate(detail.period_start)} – {fmtDate(detail.period_end)}, from {detail.books_included} client(s).
                        Your name and this exact total are recorded. If the total changes before you press the button, the
                        server refuses and you review again.
                    </p>
                    <Textarea label="Note (optional)" value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-3">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={doAuthorise}>{busy ? 'Authorising…' : `Authorise ${money(detail.total_fees)}`}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'sweep' && detail && (
                <Modal title="Sweep fees to the operating account" subtitle="Moves money out of trust — managing broker only" onClose={() => setModal(null)}>
                    <Notice tone="warn"
                        message={`This transfers up to ${money(detail.authorised_total ?? detail.total_fees)} out of client trust into the brokerage's operating account, client by client. Any client whose own balance no longer covers their fee is skipped and listed — never funded from another client.`} />
                    {operating.length === 0
                        ? <Notice tone="bad" message="No operating account is on file. Open one under Trust Accounts first." />
                        : <Select label="Operating account" value={form.destination_account_id}
                            onChange={(e) => setForm({ ...form, destination_account_id: e.target.value })}
                            options={operating.map((a) => ({ value: a.account_id, label: `${a.account_name} — ${a.bank_name}` }))} />}
                    <div className="flex justify-end gap-2 mt-3">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button variant="danger" disabled={busy || !form.destination_account_id} onClick={doSweep}>
                            {busy ? 'Sweeping…' : 'Sweep now'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===========================================================================
// Rent billings
// ===========================================================================
function BillingsTab({ api, setNotice }) {
    const [through, setThrough] = useState(TODAY);
    const [preview, setPreview] = useState(null);
    const [billings, setBillings] = useState([]);
    const [status, setStatus] = useState('outstanding');
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);

    const loadList = useCallback(async () => {
        try { setBillings(await api.get(`${API}/billings`) || []); }
        catch (e) { setNotice(errNotice(e)); }
    }, [api, setNotice]);
    useEffect(() => { loadList(); }, [loadList]);

    const doPreview = async () => {
        setBusy(true); setNotice(null);
        try { setPreview(await api.get(`${API}/ops/billings/preview?through=${through}`)); }
        catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };
    const doGenerate = async () => {
        setBusy(true); setNotice(null);
        try {
            const r = await api.post(`${API}/ops/billings/generate`, { through });
            const errs = r.leases.filter((l) => l.error);
            setNotice({ tone: errs.length ? 'warn' : 'good', title: 'Billings raised', message: r.message,
                problems: errs.map((l) => `${l.property_name} ${l.unit_number}: ${l.error}`) });
            setPreview(null); setConfirm(false); await loadList();
        } catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };

    const shown = billings.filter((b) => status === 'all' ? true
        : status === 'outstanding' ? ['open', 'partial'].includes(b.status) : b.status === status);
    const outstanding = billings.filter((b) => ['open', 'partial'].includes(b.status))
        .reduce((s, b) => s + Number(b.outstanding || 0), 0);

    return (
        <div className="space-y-5">
            <Panel title="Raise rent billings"
                subtitle="Raises each live lease's charges (rent, fees) due up to the date. Billings already on file are skipped, so running it twice cannot bill a tenant twice.">
                <div className="flex flex-wrap items-end gap-3">
                    <Input label="Through" type="date" value={through} className="mb-0"
                        onChange={(e) => { setThrough(e.target.value); setPreview(null); }} />
                    <Button variant="ghost" disabled={busy || !through} onClick={doPreview}>Preview</Button>
                    <Button disabled={busy || !preview || !preview.would_create} onClick={() => setConfirm(true)}>Raise billings…</Button>
                </div>
                {preview && (
                    <div className="mt-5">
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <Stat label="New billings" value={preview.would_create} />
                            <Stat label="Amount" value={money(preview.would_create_amount)} />
                        </div>
                        <Table columns={[
                            { key: 'property_name', label: 'Lease', render: (r) => `${r.property_name} · ${r.unit_number}${r.lease_no ? ` (${r.lease_no})` : ''}` },
                            { key: 'would_create', label: 'New', align: 'right' },
                            { key: 'would_create_amount', label: 'Amount', align: 'right', render: (r) => money(r.would_create_amount) },
                            { key: 'detail', label: 'Periods', render: (r) => r.error ? <span className="text-xs text-rose-600">{r.error}</span>
                                : r.note ? <span className="text-xs text-slate-500">{r.note}</span>
                                : <span className="text-xs text-slate-500">{r.rows.filter((x) => !x.exists).map((x) => `${x.label} ${x.period_start.slice(0, 7)}${x.proration_note ? ' (prorated)' : ''}`).join(', ') || 'all on file'}</span> },
                        ]} rows={preview.leases.map((l) => ({ ...l, _key: l.lease_id }))}
                            rowTone={(r) => (r.error ? 'bad' : null)} empty="No live leases in your portfolio." />
                    </div>
                )}
            </Panel>

            <Panel title="Billings" subtitle={`Outstanding: ${money(outstanding)}`}
                actions={<Select label="" value={status} onChange={(e) => setStatus(e.target.value)} className="mb-0"
                    options={[{ value: 'outstanding', label: 'Outstanding' }, { value: 'paid', label: 'Paid' },
                        { value: 'all', label: 'All (last 500)' }]} />}>
                <Table columns={[
                    { key: 'due_on', label: 'Due', nowrap: true, render: (r) => fmtDate(r.due_on) },
                    { key: 'property_name', label: 'Unit', render: (r) => `${r.property_name} · ${r.unit_number}` },
                    { key: 'label', label: 'Charge' },
                    { key: 'amount', label: 'Amount', align: 'right', render: (r) => money(r.amount) },
                    { key: 'outstanding', label: 'Outstanding', align: 'right', render: (r) => money(r.outstanding) },
                    { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'paid' ? 'good' : r.status === 'partial' ? 'warn' : 'slate'}>{r.status}</Pill> },
                ]} rows={shown.map((b) => ({ ...b, _key: b.billing_id }))} empty="No billings." />
            </Panel>

            {confirm && preview && (
                <Modal title="Raise billings" onClose={() => setConfirm(false)}>
                    <p className="text-sm text-slate-700">
                        Raise <b>{preview.would_create}</b> billing(s) totalling <b>{money(preview.would_create_amount)}</b> through
                        {' '}{fmtDate(through)}. Each one becomes a charge on the tenant's ledger. Any prepaid rent the tenant holds is
                        applied to the new charges automatically.
                    </p>
                    <div className="flex justify-end gap-2 mt-5">
                        <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
                        <Button disabled={busy} onClick={doGenerate}>{busy ? 'Raising…' : 'Raise billings'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===========================================================================
// 1099s
// ===========================================================================
async function downloadCsv(url, filename) {
    const token = localStorage.getItem('jwtToken');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
        let msg = `Download failed (${res.status})`;
        try { msg = (await res.json()).message || msg; } catch { /* not JSON */ }
        throw new Error(msg);
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function Tax1099Tab({ api, isMB, setNotice }) {
    const [year, setYear] = useState(LAST_YEAR);
    const [kind, setKind] = useState('owners');
    const [preview, setPreview] = useState(null);
    const [records, setRecords] = useState([]);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [thresholds, setThresholds] = useState([]);
    const [thForm, setThForm] = useState(null);

    const loadThresholds = useCallback(async () => {
        try { setThresholds(await api.get(`${API}/ops/1099/thresholds/${year}`) || []); }
        catch { setThresholds([]); }
    }, [api, year]);
    useEffect(() => { loadThresholds(); }, [loadThresholds]);
    const saveThreshold = async () => {
        setBusy(true); setNotice(null);
        try {
            const r = await api.put(`${API}/ops/1099/thresholds/${year}`, thForm);
            setNotice({ tone: 'good', message: r.message }); setThForm(null); setPreview(null);
            await loadThresholds();
        } catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };

    const loadRecords = useCallback(async () => {
        try { setRecords(await api.get(`${API}/ops/1099/records/${year}?kind=${kind}`) || []); }
        catch (e) { setRecords([]); setNotice(errNotice(e)); }
    }, [api, year, kind, setNotice]);
    useEffect(() => { setPreview(null); loadRecords(); }, [loadRecords]);

    const doPreview = async () => {
        setBusy(true); setNotice(null);
        try {
            setPreview(kind === 'owners'
                ? await api.get(`${API}/ops/1099/owners/${year}`)
                : await api.get(`${API}/vendors/1099/${year}`));
        } catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };
    const doGenerate = async () => {
        setBusy(true); setNotice(null);
        try {
            const r = kind === 'owners'
                ? await api.post(`${API}/1099/generate/${year}`, {})
                : await api.post(`${API}/vendors/1099/${year}`, {});
            setNotice({ tone: r.warning ? 'warn' : 'good', title: `${r.created} draft 1099(s) created`,
                message: r.warning || r.note || null,
                problems: (r.skipped || []).map((s) => `${s.owner || s.vendor}: ${s.reason}`) });
            setConfirm(false); setPreview(null); await loadRecords();
        } catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };
    const doDownload = async () => {
        try { await downloadCsv(`${API}/ops/1099/export/${year}?kind=${kind}`, `pm-1099-${kind}-${year}.csv`); }
        catch (e) { setNotice({ message: e.message }); }
    };

    const owners = kind === 'owners';
    const rows = !preview ? [] : owners ? preview.owners : (preview.vendors || []);
    const willCreate = !preview ? 0 : owners ? preview.would_create
        : rows.filter((v) => v.reportable).length;
    const canGenerate = owners ? isMB : true;

    return (
        <div className="space-y-5">
            <Panel title="Year-end 1099s"
                subtitle="Owners get a 1099-MISC for GROSS rents collected (not the net distributed). Vendors paid from trust get a 1099-NEC for what was actually paid in the year. Runs create DRAFT records; filing is done from Accounting → Tax Center.">
                <div className="flex flex-wrap items-end gap-3">
                    <Input label="Tax year" type="number" value={year} className="mb-0 w-32"
                        onChange={(e) => setYear(Number(e.target.value))} />
                    <Select label="Recipients" value={kind} className="mb-0" onChange={(e) => setKind(e.target.value)}
                        options={[{ value: 'owners', label: 'Owners — 1099-MISC rents' }, { value: 'vendors', label: 'Vendors — 1099-NEC' }]} />
                    <Button variant="ghost" disabled={busy} onClick={doPreview}>Preview</Button>
                    <Button disabled={busy || !preview || !canGenerate} onClick={() => setConfirm(true)}
                        title={canGenerate ? '' : 'Managing broker only'}>Generate drafts…</Button>
                    <Button variant="subtle" onClick={doDownload}>Download CSV</Button>
                </div>
                {owners && !isMB && <p className="text-xs text-slate-500 mt-2">Owner 1099s are generated by the managing broker; you can preview and download.</p>}
                {preview && (
                    <div className="mt-5">
                        {preview.threshold === null || preview.threshold === undefined
                            ? <Notice tone="bad" message={`No reporting threshold is recorded for ${year}, so nothing can be generated. The managing broker adds it under Reporting thresholds below.`} />
                            : <Notice tone={preview.threshold_confirmed ? 'info' : 'warn'}
                                message={`Threshold ${money(preview.threshold)} (${preview.threshold_authority || 'source not recorded'})${preview.threshold_caveat ? ` — ${preview.threshold_caveat}` : ''}`} />}
                        <Table columns={owners ? [
                            { key: 'owner', label: 'Owner' },
                            { key: 'tin_last4', label: 'TIN', render: (r) => (r.tin_last4 ? `…${r.tin_last4}` : '—') },
                            { key: 'gross_rents', label: 'Gross rents', align: 'right', render: (r) => money(r.gross_rents) },
                            { key: 'state', label: '', render: (r) => (
                                <span className="space-x-1">
                                    {r.existing_record_id ? <Pill tone="good">on file #{r.existing_record_id}</Pill>
                                        : r.reportable ? <Pill tone="indigo">will create</Pill> : <Pill>below threshold</Pill>}
                                    {!r.w9_on_file && <Pill tone="bad">no W-9</Pill>}
                                </span>) },
                        ] : [
                            { key: 'legal_name', label: 'Vendor' },
                            { key: 'payments', label: 'Payments', align: 'right' },
                            { key: 'paid', label: 'Paid', align: 'right', render: (r) => money(r.paid) },
                            { key: 'state', label: '', render: (r) => (
                                <span className="space-x-1">
                                    {r.reportable ? <Pill tone="indigo">reportable</Pill>
                                        : r.is_corporation ? <Pill>corporation</Pill>
                                        : !r.issue_1099 ? <Pill>no 1099</Pill> : <Pill>below threshold</Pill>}
                                    {!r.w9_on_file && <Pill tone="bad">no W-9</Pill>}
                                </span>) },
                        ]} rows={rows.map((r, i) => ({ ...r, _key: r.book_id || r.vendor_id || i }))}
                            empty={`Nothing paid or collected in ${year}.`} />
                        {!owners && <p className="text-xs text-slate-400 mt-2">Vendors already on file for the year are skipped when you generate.</p>}
                    </div>
                )}
            </Panel>

            <Panel title={`Reporting thresholds — ${year}`}
                subtitle="Set by statute and inflation-adjusted from 2027. The runs refuse a year with no threshold rather than guess one. Managing broker sets them.">
                <Table columns={[
                    { key: 'form_type', label: 'Form', nowrap: true },
                    { key: 'box_label', label: 'Box' },
                    { key: 'threshold_amount', label: 'Threshold', align: 'right', render: (r) => money(r.threshold_amount) },
                    { key: 'authority', label: 'Source', render: (r) => <span className="text-xs">{r.authority}</span> },
                    { key: 'confirmed', label: '', render: (r) => (r.confirmed ? <Pill tone="good">confirmed</Pill> : <Pill tone="warn">to confirm</Pill>) },
                ]} rows={thresholds.map((t) => ({ ...t, _key: t.form_type }))}
                    onRowClick={isMB ? (r) => setThForm({ form_type: r.form_type, threshold_amount: r.threshold_amount, authority: r.authority || '', confirmed: !!r.confirmed, notes: r.notes || '' }) : undefined}
                    empty={`No threshold recorded for ${year}.`} />
                {isMB && (
                    <div className="mt-3">
                        <Button variant="ghost" onClick={() => setThForm({ form_type: owners ? '1099-MISC' : '1099-NEC', threshold_amount: '', authority: '', confirmed: false, notes: '' })}>
                            Set a threshold…</Button>
                    </div>
                )}
            </Panel>

            {thForm && (
                <Modal title={`Reporting threshold for ${year}`} subtitle="Managing broker" onClose={() => setThForm(null)}>
                    <Select label="Form" value={thForm.form_type} onChange={(e) => setThForm({ ...thForm, form_type: e.target.value })}
                        options={[{ value: '1099-MISC', label: '1099-MISC — Box 1 rents (owners)' }, { value: '1099-NEC', label: '1099-NEC — Box 1 nonemployee compensation (vendors)' }]} />
                    <Input label="Threshold ($)" type="number" step="0.01" value={thForm.threshold_amount}
                        onChange={(e) => setThForm({ ...thForm, threshold_amount: e.target.value })} />
                    <Input label="Source (required)" value={thForm.authority} placeholder="e.g. IRS Rev. Proc. 2026-xx"
                        onChange={(e) => setThForm({ ...thForm, authority: e.target.value })} />
                    <Textarea label="Notes" value={thForm.notes} onChange={(e) => setThForm({ ...thForm, notes: e.target.value })} />
                    <label className="flex gap-2 text-sm mb-3"><input type="checkbox" checked={!!thForm.confirmed}
                        onChange={(e) => setThForm({ ...thForm, confirmed: e.target.checked })} /> Confirmed with the accountant / IRS</label>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setThForm(null)}>Cancel</Button>
                        <Button disabled={busy || !thForm.threshold_amount || !String(thForm.authority).trim()} onClick={saveThreshold}>
                            {busy ? 'Saving…' : 'Save threshold'}</Button>
                    </div>
                </Modal>
            )}

            <Panel title={`Draft records — ${year}`} subtitle="Change status (draft → ready → filed) under Accounting → Tax Center → 1099 Filings.">
                <Table columns={[
                    { key: 'record_id', label: '#', nowrap: true },
                    { key: 'form_type', label: 'Form', nowrap: true },
                    { key: 'counterparty_name', label: 'Recipient' },
                    { key: 'counterparty_tin_last4', label: 'TIN', render: (r) => (r.counterparty_tin_last4 ? `…${r.counterparty_tin_last4}` : '—') },
                    { key: 'amount', label: 'Amount', align: 'right', render: (r) => money(r.amount) },
                    { key: 'w9_on_file', label: 'W-9', render: (r) => (r.w9_on_file ? <Pill tone="good">on file</Pill> : <Pill tone="bad">missing</Pill>) },
                    { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'filed' ? 'good' : 'warn'}>{r.status}</Pill> },
                ]} rows={records.map((r) => ({ ...r, _key: r.record_id }))} empty="No 1099 records for this year yet." />
            </Panel>

            {confirm && preview && (
                <Modal title={`Generate ${owners ? 'owner 1099-MISC' : 'vendor 1099-NEC'} drafts for ${year}`} onClose={() => setConfirm(false)}>
                    <p className="text-sm text-slate-700">
                        Creates about <b>{willCreate}</b> draft record(s). Recipients already on file for {year} are skipped, so
                        running this again will not duplicate a form. Nothing is sent to the IRS or to anyone — drafts are filed
                        from the Tax Center.
                    </p>
                    {rows.some((r) => !r.w9_on_file) && (
                        <Notice tone="warn" message="Some recipients have no W-9 on file. Their drafts are created but cannot be filed with a certified TIN until it is collected." />
                    )}
                    <div className="flex justify-end gap-2 mt-5">
                        <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
                        <Button disabled={busy} onClick={doGenerate}>{busy ? 'Generating…' : 'Generate drafts'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===========================================================================
// Conversion check
// ===========================================================================
function ConversionTab({ api, setNotice }) {
    const [asOf, setAsOf] = useState(TODAY);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [clients, setClients] = useState(null);

    const runSweep = async () => {
        setBusy(true); setNotice(null); setClients(null);
        try { setResult(await api.post(`${API}/ops/conversion-sweep`, { as_of: asOf })); }
        catch (e) { setNotice(errNotice(e)); } finally { setBusy(false); }
    };
    const openAccount = async (f) => {
        try {
            const r = await api.get(`${API}/trust-accounts/${f.account_id}/client-balances?as_of=${result.as_of}`);
            setClients({ name: f.account_name, ...r });
        } catch (e) { setNotice(errNotice(e)); }
    };

    return (
        <div className="space-y-5">
            <Panel title="Conversion check"
                subtitle="Reads every client ledger in every open trust account as of a date and lists any that is negative. A negative client ledger means another client's money paid that client's bills — conversion — even if the account as a whole reconciles. Reading only; each run is recorded in the activity log.">
                <div className="flex flex-wrap items-end gap-3">
                    <Input label="As of" type="date" value={asOf} className="mb-0" onChange={(e) => setAsOf(e.target.value)} />
                    <Button disabled={busy || !asOf} onClick={runSweep}>{busy ? 'Checking…' : 'Run check'}</Button>
                </div>
                {result && (
                    <div className="mt-5">
                        {result.clean
                            ? <Notice tone="good" title="No negative client ledger"
                                message={`${result.accounts_checked} open trust account(s) checked as of ${fmtDate(result.as_of)}.`} />
                            : <Notice tone="bad" title={`${result.findings.length} negative client ledger(s)`}
                                message="Each one must be cured by depositing the client's own funds (or the broker's, documented) and reported as the module and the Division's rules require. Do not move another client's money to fix it." />}
                        {!result.clean && (
                            <Table columns={[
                                { key: 'owner_name', label: 'Client' },
                                { key: 'account_name', label: 'Trust account' },
                                { key: 'trust_cash', label: 'Ledger balance', align: 'right', render: (r) => <span className="text-rose-700 font-semibold">{money(r.trust_cash)}</span> },
                            ]} rows={result.findings.map((f) => ({ ...f, _key: `${f.account_id}-${f.owner_id}` }))}
                                onRowClick={openAccount} rowTone={() => 'bad'} />
                        )}
                    </div>
                )}
            </Panel>

            {clients && (
                <Modal title={`Client balances — ${clients.name}`} subtitle={`As of ${fmtDate(clients.as_of)}`} onClose={() => setClients(null)} wide>
                    <Table columns={[
                        { key: 'owner_name', label: 'Client' },
                        { key: 'trust_cash', label: 'Trust cash', align: 'right', render: (r) => money(r.trust_cash) },
                        { key: 'deposits_held', label: 'Deposits held', align: 'right', render: (r) => money(r.deposits_held) },
                        { key: 'converted', label: '', render: (r) => (r.converted ? <Pill tone="bad">negative</Pill> : null) },
                    ]} rows={(clients.clients || []).map((c) => ({ ...c, _key: c.book_id }))}
                        rowTone={(r) => (r.converted ? 'bad' : null)} />
                    <p className="text-sm text-slate-600 mt-3">Account total: {money(clients.total_trust_cash)} trust cash, {money(clients.total_deposits_held)} deposits held.</p>
                </Modal>
            )}
        </div>
    );
}
